import React, { useState, useEffect, useRef } from 'react'
import { useTransition, animated } from 'react-spring'

import messages from 'components/ResultMessage/messages'

import { useQuizContext } from 'store/QuizStore'
import { setScreen } from 'store/actions'

import { FluidBox, FlexBox } from 'styled'
import ThemesPanel from './ThemesPanel'
import AnswersPanel from './AnswersPanel'
import ChartPanel from './ChartPanel'

import {
  Root,
  Header,
  ResultCircle,
  SideColumn,
  CenterColumn,
  ResultTransitionWrapper,
  BackLink,
  ScrollbarsWrapper,
  ThumbY,
  TrackY,
  TrackX,
  ScrollbarsInner,
  ContentRow,
  BodyRow,
  NavButton,
  CenterInner,
} from './Results.styled.js'

import { ReactComponent as PreviousIcon } from './../../assets/nav-left.svg'
import { ReactComponent as NextIcon } from './../../assets/nav-right.svg'
import { ReactComponent as HomeIcon } from './../../assets/home-icon.svg'
import { useMedia } from 'react-use'

const apiHost = process.env.REACT_APP_API_SERVER

const getResults = async (id, cb) => {
  try {
    const response = await fetch(`${apiHost}/answers/${id}`)
    const res = await response.json()

    cb(r => {
      r[id] = res
      return { ...r }
    })
  } catch (error) {
    console.log('could not fetch results', error)
  }
}

const calcResult = (questions, answers) => {
  let r = 0
  questions.forEach((question, id) => {
    if (question.correct === answers[id]) {
      r++
    }
  })
  return r
}

const msgMap = {
  7: 0,
  6: 0,
  5: 1,
  4: 1,
  3: 1,
  2: 2,
  1: 2,
  0: 3,
}

const AnimatedWrapper = animated(
  ({ transitionState, navDirection, result, question, userAnswer, isChart }) => {
    const leaving = transitionState > 1.1
    const entering = transitionState < 0.9

    return (
      <ResultTransitionWrapper px={[16, 40, 40, 80]}>
        {isChart ? (
          <ChartPanel
            result={result}
            question={question}
            userAnswer={userAnswer}
            entering={entering}
            leaving={leaving}
            navDirection={navDirection}
          ></ChartPanel>
        ) : (
          <AnswersPanel
            result={result}
            question={question}
            userAnswer={userAnswer}
            entering={entering}
            leaving={leaving}
            navDirection={navDirection}
          ></AnswersPanel>
        )}
      </ResultTransitionWrapper>
    )
  }
)

const Results = () => {
  const { themes, questions, answers, numQuestions, reset, dispatch } = useQuizContext()
  const [answerId, setAnswerId] = useState()
  const [navDirection, setNavDirection] = useState(1)
  const [results, setResults] = useState({})
  const [selectedQuestion, setSelectedQuestion] = useState(0)
  const [scrollShadows, setShadows] = useState({ top: false, bottom: false })
  const isWide = useMedia('(min-width: 1024px)')

  const scrollbars = useRef()
  const result = calcResult(questions, answers)

  useEffect(() => {
    if (!results[selectedQuestion]) {
      getResults(selectedQuestion, setResults)
    }
  }, [selectedQuestion, results, setResults])

  useEffect(() => {
    if (results[selectedQuestion] !== undefined) {
      setAnswerId(selectedQuestion)
    }
  }, [selectedQuestion, results, setAnswerId])

  const answersTransition = useTransition(answerId, null, {
    initial: { state: 0.8 },
    from: { state: 0 },
    enter: { state: 1 },
    leave: {
      state: 2,
    },
    config: {
      duration: 1000,
    },
  })

  const navigate = dir => {
    const next = selectedQuestion + dir
    setNavDirection(dir)
    setSelectedQuestion(next)
    scrollbars.current.scrollToTop()
  }

  const selectQuestion = id => {
    const dir = Math.sign(id - selectedQuestion)
    setNavDirection(dir)
    setSelectedQuestion(id)
    scrollbars.current.scrollToTop()
  }

  const handleScroll = values => {
    const { scrollTop, scrollHeight, clientHeight } = values
    const top = scrollTop > 0
    const bottomScrollTop = scrollHeight - clientHeight
    const bottom = bottomScrollTop > scrollTop
    setShadows({ top, bottom })
  }

  return (
    <Root>
      <ContentRow h={120}>
        <SideColumn px={[0, 40, 40, 80]} w={[400, 400, 380, 465]}></SideColumn>
        <CenterColumn>
          <CenterInner px={[0, 40, 40, 80]} py={[20, 40, 40, 80]}>
            <Header>
              <ResultCircle>
                <FluidBox fs="textSmall" color="primary">
                  {result}/{numQuestions}
                </FluidBox>
              </ResultCircle>
              <div>
                <FluidBox fs={12} color="primary" tt="uppercase" lh={1.25}>
                  Quiz Recap
                </FluidBox>
                <FluidBox color="primary" fw="bold" lh={1.25}>
                  {messages[msgMap[result]].title}
                </FluidBox>
              </div>
            </Header>
          </CenterInner>
        </CenterColumn>
      </ContentRow>
      <BodyRow shadows={scrollShadows}>
        <div className="Inner">
          <ScrollbarsWrapper
            ref={scrollbars}
            autoHide={true}
            onUpdate={handleScroll}
            renderTrackHorizontal={() => {
              return <TrackX />
            }}
            renderTrackVertical={() => {
              return <TrackY />
            }}
            renderThumbVertical={({ props }) => {
              return <ThumbY {...props} />
            }}
          >
            <ScrollbarsInner>
              <SideColumn px={[0, 40, 40, 80]} w={[400, 400, 380, 465]}>
                <ThemesPanel
                  themes={themes}
                  answers={answers}
                  questions={questions}
                  selectedQuestion={selectedQuestion}
                  setQuestion={selectQuestion}
                ></ThemesPanel>
              </SideColumn>
              <CenterColumn>
                <CenterInner>
                  {answersTransition.map(
                    ({ item, key, props }) =>
                      item !== null && (
                        <AnimatedWrapper
                          key={key}
                          transitionState={props.state}
                          navDirection={navDirection}
                          result={results[item]}
                          question={questions[item]}
                          userAnswer={answers[item]}
                          isChart={questions[item].questionType === 'chart'}
                        ></AnimatedWrapper>
                      )
                  )}
                </CenterInner>
              </CenterColumn>
            </ScrollbarsInner>
          </ScrollbarsWrapper>
        </div>
      </BodyRow>
      <ContentRow h={[80, 80, 100, 100]} isBottom={true}>
        <SideColumn px={[0, 40, 40, 80]} w={[400, 400, 380, 465]}>
          <FlexBox h="100%" align="center">
            <BackLink fs={12} fw="bold" onClick={() => setScreen(dispatch, 'intro')}>
              Back to intro page
            </BackLink>
          </FlexBox>
        </SideColumn>
        <CenterColumn>
          <CenterInner px={[16, 40, 40, 80]}>
            <FlexBox h="100%" align="center" justify="space-between">
              <NavButton enabled={selectedQuestion !== 0} onClick={() => navigate(-1)}>
                <span>Previous</span>
                <div className="Icon">
                  <PreviousIcon></PreviousIcon>
                </div>
              </NavButton>
              <NavButton enabled={true} onClick={reset} className="HomeButton">
                <span>Re-take quiz</span>
                <div className="Icon">
                  <HomeIcon></HomeIcon>
                </div>
              </NavButton>
              {isWide ? (
                <NavButton
                  enabled={true}
                  onClick={selectedQuestion < numQuestions - 1 ? () => navigate(1) : () => reset()}
                >
                  <span>{selectedQuestion < numQuestions - 1 ? 'Next' : 'Re-take quiz'}</span>
                  <div className="Icon">
                    <NextIcon></NextIcon>
                  </div>
                </NavButton>
              ) : (
                <NavButton
                  enabled={selectedQuestion < numQuestions - 1}
                  onClick={() => navigate(1)}
                >
                  <span>Next</span>
                  <div className="Icon">
                    <NextIcon></NextIcon>
                  </div>
                </NavButton>
              )}
            </FlexBox>
          </CenterInner>
        </CenterColumn>
      </ContentRow>
    </Root>
  )
}

export default Results
